import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  message,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Badge,
  Popconfirm,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  DeleteOutlined,
  UploadOutlined,
  MessageOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import API from '../services/api'; // 导入统一的API实例
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

const UserTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: undefined,
    priority: undefined,
    search: ''
  });

  const [createForm] = Form.useForm();

  // 状态映射
  const statusMap = {
    open: { text: '待处理', color: 'orange' },
    in_progress: { text: '处理中', color: 'blue' },
    waiting_customer: { text: '等待客户', color: 'purple' },
    closed: { text: '已关闭', color: 'green' }
  };

  // 优先级映射
  const priorityMap = {
    low: { text: '低', color: 'green' },
    medium: { text: '中', color: 'orange' },
    high: { text: '高', color: 'red' },
    urgent: { text: '紧急', color: 'volcano' }
  };



  // 获取工单列表
  const fetchTickets = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await API.get('/api/tickets', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          ...filters,
          ...params
        }
      });

      if (response.data.code === 0) {
        setTickets(response.data.data.tickets);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total
        }));
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('获取工单列表失败: ' + error.message);
    }
    setLoading(false);
  }, [pagination.page, pagination.limit, filters]);



  // 创建工单
  const handleCreateTicket = async (values) => {
    try {
      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('description', values.description);
      formData.append('priority', values.priority);
      
      // 只有当标签不为空且不是空数组时才添加到formData
      if (values.tags && Array.isArray(values.tags) && values.tags.length > 0) {
        formData.append('tags', JSON.stringify(values.tags));
      }

      // 处理敏感数据
      if (values.sensitiveData && values.sensitiveData.length > 0) {
        formData.append('sensitiveData', JSON.stringify(values.sensitiveData));
      }

      // 处理文件上传
      if (values.attachments && values.attachments.fileList) {
        values.attachments.fileList.forEach(file => {
          if (file.originFileObj) {
            formData.append('attachments', file.originFileObj);
          }
        });
      }

      const response = await API.post('/api/tickets', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.code === 0) {
        message.success('工单创建成功');
        setCreateModalVisible(false);
        createForm.resetFields();
        fetchTickets();
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('创建工单失败: ' + error.message);
    }
  };





  // 关闭工单
  const handleCloseTicket = async (ticketId) => {
    try {
      const response = await API.post(`/api/tickets/${ticketId}/close`);
      if (response.data.code === 0) {
        message.success('工单已关闭');
        fetchTickets();
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('关闭工单失败: ' + error.message);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '工单编号',
      dataIndex: '_id',
      key: '_id',
      render: (id) => <Text copyable>{id.slice(-8)}</Text>
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={statusMap[status]?.color}>{statusMap[status]?.text}</Tag>
      )
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => (
        <Tag color={priorityMap[priority]?.color}>{priorityMap[priority]?.text}</Tag>
      )
    },
    {
      title: '回复数',
      dataIndex: 'replies',
      key: 'replies',
      width: 100,
      align: 'center',
      render: (replies) => {
        const count = replies?.length || 0;
        if (count === 0) {
          return (
            <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
              <MessageOutlined style={{ marginRight: '4px' }} />
              暂无回复
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Badge 
              count={count} 
              style={{ 
                backgroundColor: count > 5 ? '#f5222d' : count > 2 ? '#fa8c16' : '#52c41a',
                borderRadius: '10px',
                minWidth: '20px',
                height: '20px',
                lineHeight: '20px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            />
            <MessageOutlined style={{ marginLeft: '6px', color: '#1890ff', fontSize: '14px' }} />
          </div>
        );
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="查看详情">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => navigate(`/user/tickets/${record._id}`)}
            />
          </Tooltip>
          {record.status !== 'closed' && (
            <Tooltip title="关闭工单">
              <Popconfirm
                title="确认关闭此工单吗？"
                onConfirm={() => handleCloseTicket(record._id)}
                okText="确认"
                cancelText="取消"
              >
                <Button
                  icon={<CloseCircleOutlined />}
                  size="small"
                  type="default"
                  danger
                >关闭</Button>
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3}>我的工单</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            创建工单
          </Button>
        </div>

        {/* 筛选条件 */}
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={6}>
            <Select
              placeholder="选择状态"
              allowClear
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
            >
              <Option value="open">待处理</Option>
              <Option value="in_progress">处理中</Option>
              <Option value="waiting_customer">等待客户</Option>
              <Option value="closed">已关闭</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="选择优先级"
              allowClear
              style={{ width: '100%' }}
              value={filters.priority}
              onChange={(value) => setFilters(prev => ({ ...prev, priority: value }))}
            >
              <Option value="low">低</Option>
              <Option value="medium">中</Option>
              <Option value="high">高</Option>
              <Option value="urgent">紧急</Option>
            </Select>
          </Col>
          <Col span={8}>
            <Input.Search
              placeholder="搜索工单标题或描述"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              onSearch={() => fetchTickets({ page: 1 })}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Button onClick={() => fetchTickets({ page: 1 })}>搜索</Button>
          </Col>
        </Row>

        {/* 工单表格 */}
        <Table
          columns={columns}
          dataSource={tickets}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, page, limit: pageSize }));
            }
          }}
        />
      </Card>

      {/* 创建工单模态框 */}
      <Modal
        title="创建新工单"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={800}
        centered={true}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateTicket}
        >
          <Form.Item
            label="标题"
            name="title"
            rules={[{ required: true, message: '请输入工单标题' }]}
          >
            <Input placeholder="请输入工单标题" />
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
            rules={[{ required: true, message: '请输入工单描述' }]}
          >
            <TextArea rows={4} placeholder="请详细描述您遇到的问题" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="优先级"
                name="priority"
                initialValue="medium"
              >
                <Select>
                  <Option value="low">低</Option>
                  <Option value="medium">中</Option>
                  <Option value="high">高</Option>
                  <Option value="urgent">紧急</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="标签"
                name="tags"
                initialValue={undefined}
              >
                <Select
                  mode="tags"
                  placeholder="添加标签（可选）"
                  style={{ width: '100%' }}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="附件"
            name="attachments"
          >
            <Upload
              multiple
              beforeUpload={() => false}
              maxCount={10}
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            label="敏感信息"
            name="sensitiveData"
          >
            <Form.List name="sensitiveData">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row key={key} gutter={16} align="middle">
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'label']}
                          rules={[{ required: true, message: '请输入标签' }]}
                        >
                          <Input placeholder="信息标签" />
                        </Form.Item>
                      </Col>
                      <Col span={14}>
                        <Form.Item
                          {...restField}
                          name={[name, 'value']}
                          rules={[{ required: true, message: '请输入值' }]}
                        >
                          <Input.Password placeholder="敏感信息内容" />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Button
                          type="text"
                          size="small"
                          icon={<DeleteOutlined />}
                          danger
                          onClick={() => remove(name)}
                          style={{ borderRadius: '4px' }}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                      style={{ 
                        width: '100%',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      添加敏感信息
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                创建工单
              </Button>
              <Button onClick={() => {
                setCreateModalVisible(false);
                createForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>


    </div>
  );
};

export default UserTickets; 